/* eslint-disable import/no-named-as-default-member */
// eslint-disable-next-line import/no-named-as-default
import store from '../store';

const moment = require('moment');

/**
 * A class to handle form filler functions.
 */
class FormFillerService {
    /**
     * Creates an appropriate action.
     *
     * @param action
     * @returns {{created_at: string, type: string}}
     */
    static createAction(action = {}) {
        const defaults = {
            type: 'idle',
            created_at: moment().format(),
            request_id: moment().valueOf(),
            version: '1.0',
        };

        return { ...defaults, ...action };
    }

    /**
     * Dispatches the form filler update.
     *
     * @param options
     *   - @b recipientId
     *     The recipient ID; defaults to null. Returns all recipients if not set, otherwise only returns the given
     *     recipient.
     *   - @b event
     *     The event; defaults to null. If the passed event contains an `data.request_id` field it is used for the
     *     request id.
     *   .
     */
    static async dispatchFormFillerUpdate(options = { recipientId: null, data: null, origin: null }) {
        const { recipientId, data, origin } = options;

        // eslint-disable-next-line no-undef
        await Vue.backendApi().user().getFormFillerData(recipientId).then((response) => {
            const requestId = FormFillerService.getRequestId(data);

            // eslint-disable-next-line no-undef
            const payload = FormFillerService.createAction({
                type: 'AUTO_FILLER_UPDATE',
                origin: origin || null,
                request_id: requestId,
                data: response.data,
            });

            window.postMessage(payload, '*');

            return response.data;
        });
    }

    /**
     * Open Third Party Companies websites on Forgot Password Page
     */
    static async resetMasterPass() {
        const { data } = await Vue.backendApi().user().formFillerUpdate3poList();

        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_UPDATE_3PO_LIST',
            data: data.signed.data,
        });

        window.postMessage(payload, '*');

        return Promise.resolve(payload);
    }

    /**
     * Send Password Reset Link to Extension
     */
    static async sendPasswordResetLink(event) {
        const { data, unsigned } = event.signed;
        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_PASSWORD_RESET_LINK',
            data,
            unsigned,
        });

        window.postMessage(payload, '*');

        return Promise.resolve(payload);
    }

    /**
     * Send Email Confirmation Link to Extension
     */
    static async sendEmailConfirmationLink(event) {
        const {
            signed: {
                data,
            },
            unsigned,
        } = event;
        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_EMAIL_CONFIRMATION_LINK',
            data,
            unsigned,
        });

        window.postMessage(payload, '*');

        return Promise.resolve(payload);
    }

    /**
     * Send Problem Report to backend
     */
    static async sendProblemReport({ data: { data } }) {
        await Vue.backendApi()
            .extensionProblemReports()
            .create(data);

        return Promise.resolve();
    }

    /**
     * Send Problem Report Replied Message to Extension
     */
    static async sendProblemReportReplied(event) {
        const {
            signed: {
                data,
            },
            unsigned,
        } = event;
        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_SEND_PROBLEM_REPORT_REPLIED',
            data,
            unsigned,
        });

        window.postMessage(payload, '*');

        return Promise.resolve(payload);
    }

    /**
     * Dispatches a payment event.
     *
     * @param order
     *
     * @returns {Promise<string>}
     */
    static async dispatchPaymentEvent(orderId, type = '3po', website = null, eagerLoad = {}) {
        Vue.backendApi().user().getFormFillerToPayRecipient(orderId, type, eagerLoad).then((response) => {
            const signedResponseData = response.data.signed;

            if (response.data.unsigned) {
                console.log('Unsigned Data', response.data.unsigned);
            }

            const payload = FormFillerService.createAction({
                type: 'AUTO_FILLER_PAYMENT_EVENT',
                data: signedResponseData.data,
                website,
                unsigned: response.data.unsigned,
            });

            // if we have unsigned data then we're in a dev or testing environment
            if (response.data.unsigned) {
                console.log("Sending window.postMessage with this payload: ", payload);
            }

            window.postMessage(payload, '*');
        }).catch((error) => {
            console.error(error);
            return Promise.reject(error);
        });

        return Promise.resolve('ok');
    }

    /**
     * Stores the encrypted data.
     *
     * @param data
     */
    static storeEncryptedData(data, origin = null) {
        // eslint-disable-next-line no-undef
        return Vue.backendApi().user().storeFormFillerData(data).then((response) => {
            const { data } = response;
            const requestId = this.getRequestId(data);

            const payload = FormFillerService.createAction({
                type: 'AUTO_FILLER_RESPONSE',
                hash: data.hash,
                nonce: data.nonce,
                signature: data.signature,
                origin: origin || null,
                request_id: requestId,
                version: data.version,
                data: data.sender,
            });
            return Promise.resolve(payload);
        })
            .catch((error) => {
                console.error(`Unable to store data, got: ${error}`);
                return Promise.reject(error);
            });
    }

    /**
     * Get random 32 bit string.
     *
     * @returns {string}
     */
    static random32bit() {
        let u = new Uint32Array(1);
        window.crypto.getRandomValues(u);
        let str = u[0].toString(16).toLowerCase();
        return '00000000'.slice(str.length) + str;
    }

    /**
     * Gets a request ID.
     *
     * If the data does not contain a request ID, then the current UNIX timestamp in milliseconds concatenated with a
     * period and a random 32 bit number (represented in hex) is used.
     *
     * @param data
     * @returns {number}
     */
    static getRequestId(data) {
        let requestId;
        if (data && Object.keys(data).indexOf('request_id') !== -1) {
            requestId = data.request_id;
        } else {
            // eslint-disable-next-line no-undef
            requestId = `${moment().valueOf()}.${FormFillerService.random32bit()}`;
        }
        return requestId;
    }

    /**
     * Send a ping.
     *
     * @param origin
     */
    static ping(origin = null) {
        console.log(`Sending ping at ${moment()}`);

        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_PING',
            origin: origin || 'AUTO_FILLER_PING',
            request_id: moment().valueOf(),
            data: 'ping',
        });

        console.log(payload);

        return window.postMessage(payload, '*');
    }

    static async getBrands(event) {
        const { data } = await Vue.backendApi().user().formFillerGetBrands();

        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_BRANDS',
            data: data.signed.data,
            tabId: event.data.tabId,
        });

        window.postMessage(payload, '*');
    }

    static async dispatchLogoutEvent() {
        const { data } = await Vue.backendApi().user().formFillerUserLogout();

        const payload = FormFillerService.createAction({
            type: 'AUTO_FILLER_USER_LOGOUT',
            data: data.signed.data,
        });

        window.postMessage(payload, '*');
    }

    /**
     * gets password from extenstion for a given email and hostname
     * @param {string} email
     * @param {string} hostname
     */
    static fetchPassword(email, hostname) {
        const maxTimeout = 20;

        return new Promise(async (resolve, reject) => {
            const windowMessageListener = (event) => {
                if (event.data.type === 'AUTO_FILLER_PASSWORD_FETCHED') {
                    if (event.data.error) {
                        reject(event.data.error);
                        return;
                    }
                    
                    resolve(event.data.password);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);

            try {
                const { data } = await Vue.backendApi().user().formFillerFetchPassword({
                    hostname,
                    email,
                });

                const payload = FormFillerService.createAction({
                    type: 'AUTO_FILLER_FETCH_PASSWORD',
                    data: data.signed.data,
                });

                window.postMessage(payload, '*');
            } catch (err) {
                reject(err);
            }

            setTimeout(() => {
              reject('Extension did not reply');
            }, maxTimeout * 1000);
        });
    }

    /**
     * Generates new password in extenstion and stores email and generated password for a given hostname
     * @param {string} hostname
     */
    static generatePassword(hostname) {
        const maxTimeout = 20;

        return new Promise(async (resolve, reject) => {
            const windowMessageListener = (event) => {
                if (event.data.type === 'AUTO_FILLER_PASSWORD_GENERATED') {
                    if (event.data.error) {
                        reject(event.data.error);
                        return;
                    }
                    
                    resolve(event.data.password);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);

            try {
                const { data } = await Vue.backendApi().user().formFillerGeneratePassword({
                  hostname,
                });

                const payload = FormFillerService.createAction({
                    type: 'AUTO_FILLER_GENERATE_PASSWORD',
                    data: data.signed.data,
                });

                window.postMessage(payload, '*');
            } catch (err) {
                reject(err);
            }

            setTimeout(() => {
                reject('Extension did not reply');
            }, maxTimeout * 1000);
        });
    }

    static fetchCredentials(hostname) {
        const maxTimeout = 20;

        return new Promise(async (resolve, reject) => {
            const windowMessageListener = (event) => {
                if (event.data.type === 'AUTO_FILLER_CREDENTIALS_FETCHED') {
                    if (event.data.error) {
                        reject(event.data.error);
                        return;
                    }
                    
                    resolve(event.data);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);

            try {
                const { data } = await Vue.backendApi().user().formFillerFetchCredentials({
                  hostname,
                });

                const payload = FormFillerService.createAction({
                    type: 'AUTO_FILLER_FETCH_CREDENTIALS',
                    data: data.signed.data,
                });

                window.postMessage(payload, '*');
            } catch (err) {
                reject(err);
            }

            setTimeout(() => {
              reject('Extension did not reply');
            }, maxTimeout * 1000);
        });
    }

    static saveCredentials(email, password, hostname) {
        const maxTimeout = 20;

        return new Promise(async (resolve, reject) => {
            const windowMessageListener = (event) => {
                if (event.data.type === 'AUTO_FILLER_CREDENTIALS_SAVED') {
                    if (event.data.error) {
                        reject(event.data.error);
                        return;
                    }
                    
                    resolve(event.data);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);
            try {
                const { data } = await Vue.backendApi().user().formFillerSaveCredentials({
                    hostname,
                    email,
                    password,
                });

                const payload = FormFillerService.createAction({
                    type: 'AUTO_FILLER_SAVE_CREDENTIALS',
                    data: data.signed.data,
                });

                window.postMessage(payload, '*');
            } catch (err) {
                reject(err);
            }

            setTimeout(() => {
              reject('Extension did not reply');
            }, maxTimeout * 1000);
        });
    }

    static checkMasterPassword() {
        const maxTimeout = 10;

        return new Promise(async (resolve, reject) => {
            const windowMessageListener = (event) => {
                if (event.data.type === 'MASTER_PASSWORD_CHECKED') {
                    resolve(event.data.result);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);

            const payload = FormFillerService.createAction({
                type: 'CHECK_MASTER_PASSWORD',
            });
            window.postMessage(payload, '*');

            setTimeout(() => {
              reject('Extension did not reply');
            }, maxTimeout * 1000);
        });
    }

    static async showGuide() {
        const payload = FormFillerService.createAction({
            type: 'SHOW_GUIDE',
            data: {},
        });

        window.postMessage(payload, '*');
    }

    static async getPasswordDatabase(event) {
        const { data } = await Vue.backendApi().user().getFormFillerData();

        const payload = FormFillerService.createAction({
            type: 'STICKY_NOTE_PWD_DB',
            data,
            tabId: event.data.tabId,
        });

        window.postMessage(payload, '*');
    }
}

export default FormFillerService;
